import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { findSuppliers, saveSupplier } from "../services/sales";
import { addToSuggestions, SupplierSuggestion } from "../suggestions";
import { selectCanEditSuppliers } from "./auth";
import {
  showErrorNotification,
  showSuccessNotification,
} from "./notifications";

const suppliersAdapter = createEntityAdapter({
  sortComparer: (a, b) => a.name.localeCompare(b.name),
});

const initialStateEdit = {
  id: null,
  isNew: false,
  isSaving: false,
};
const initialState = {
  ...suppliersAdapter.getInitialState(),
  edit: initialStateEdit,
};

const suppliersSlice = createSlice({
  name: "suppliers",
  initialState,
  reducers: {
    edit: (state, { payload }) => {
      state.edit.id = payload.id;
      state.edit.isNew = !!payload.isNew;
    },
    cancelEdit: (state) => ({ ...state, edit: initialStateEdit }),
    setSuppliers: suppliersAdapter.setAll,
  },
  extraReducers: (builder) => {
    builder
      .addCase(save.fulfilled, (state, { payload }) =>
        suppliersAdapter.setOne({ ...state, edit: initialStateEdit }, payload),
      )
      .addCase(save.pending, (state) => {
        state.edit.isSaving = true;
      })
      .addCase(save.rejected, (state) => {
        state.edit.isSaving = false;
      });
  },
});

export default suppliersSlice.reducer;

export const { cancelEdit, edit, setSuppliers } = suppliersSlice.actions;

export const isValidSupplier = (supplier) => supplier?.name?.length >= 3;

export const selectors = suppliersAdapter.getSelectors(
  (state) => state.suppliers,
);

export const selectEditSupplierAuthorizer = createSelector(
  selectCanEditSuppliers,
  (canEditSuppliers) => {
    return (supplier) => !supplier.readOnly && canEditSuppliers;
  },
);

export const selectEditedSupplierId = (state) => state.suppliers.edit.id;

export const selectEditedSupplierIsNew = (state) =>
  !!state.suppliers.edit.isNew;

export const selectSupplierEditOpen = (state) => !!state.suppliers.edit.id;

export const selectSupplierIsSaving = (state) => state.suppliers.edit.isSaving;

export const loadSuppliers = createAsyncThunk(
  "suppliers/loadAll",
  (_, { dispatch }) =>
    findSuppliers().then((suppliers) => {
      addToSuggestions(suppliers.map(SupplierSuggestion));
      dispatch(setSuppliers(suppliers));
    }),
);

const API_ERRORS = {
  400: "You provided incomplete or invalid supplier data",
};

export const save = createAsyncThunk(
  "suppliers/save",
  (supplier, { dispatch, getState }) => {
    const id = selectEditedSupplierId(getState());
    const isNewSupplier = selectEditedSupplierIsNew(getState());
    const payload = {
      ...supplier,
      id,
    };
    return saveSupplier(payload)
      .then((savedSupplier) => {
        if (isNewSupplier) {
          addToSuggestions([SupplierSuggestion(savedSupplier)]);
        }
        dispatch(showSuccessNotification("Supplier saved successfully"));
        return savedSupplier;
      })
      .catch((err) => {
        const message = API_ERRORS[err.status] || err.message;
        dispatch(showErrorNotification(message));
        throw err;
      });
  },
);
